// Incremental SHA-256.
//
// WebCrypto's `crypto.subtle.digest` needs the whole input as one buffer, and
// a LineageOS zip is well over a gigabyte — reading that into memory just to
// hash it is a good way to make the tab die on a modest laptop. This streams
// the file in chunks instead, and gives us a progress callback for free.

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

class Sha256 {
  private h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ]);
  private w = new Uint32Array(64);
  private buf = new Uint8Array(64);
  private bufLen = 0;
  private total = 0;

  update(data: Uint8Array): void {
    this.total += data.length;
    let offset = 0;

    // Top up a partial block left over from last time.
    if (this.bufLen > 0) {
      const take = Math.min(64 - this.bufLen, data.length);
      this.buf.set(data.subarray(0, take), this.bufLen);
      this.bufLen += take;
      offset = take;
      if (this.bufLen < 64) return;
      this.block(this.buf, 0);
      this.bufLen = 0;
    }

    for (; offset + 64 <= data.length; offset += 64) this.block(data, offset);

    if (offset < data.length) {
      this.buf.set(data.subarray(offset));
      this.bufLen = data.length - offset;
    }
  }

  digest(): string {
    const total = this.total;
    // Padding: 0x80, zeroes, then the message length in bits as a 64-bit
    // big-endian integer.
    const tail = new Uint8Array(this.bufLen < 56 ? 64 : 128);
    tail.set(this.buf.subarray(0, this.bufLen));
    tail[this.bufLen] = 0x80;

    // Splitting before the multiply keeps us inside Number's exact-integer
    // range for any file size a browser could plausibly hold.
    const hi = Math.floor(total / 0x20000000);
    const lo = (total % 0x20000000) * 8;
    const view = new DataView(tail.buffer);
    view.setUint32(tail.length - 8, hi, false);
    view.setUint32(tail.length - 4, lo >>> 0, false);

    for (let i = 0; i < tail.length; i += 64) this.block(tail, i);

    let out = "";
    for (let i = 0; i < 8; i++) out += (this.h[i]! >>> 0).toString(16).padStart(8, "0");
    return out;
  }

  private block(p: Uint8Array, off: number): void {
    const w = this.w;
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4;
      w[i] = (p[j]! << 24) | (p[j + 1]! << 16) | (p[j + 2]! << 8) | p[j + 3]!;
    }
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15]!;
      const b = w[i - 2]!;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) | 0;
    }

    let a = this.h[0]!, b = this.h[1]!, c = this.h[2]!, d = this.h[3]!;
    let e = this.h[4]!, f = this.h[5]!, g = this.h[6]!, h = this.h[7]!;

    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + s1 + ch + K[i]! + w[i]!) | 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }

    this.h[0] = (this.h[0]! + a) | 0;
    this.h[1] = (this.h[1]! + b) | 0;
    this.h[2] = (this.h[2]! + c) | 0;
    this.h[3] = (this.h[3]! + d) | 0;
    this.h[4] = (this.h[4]! + e) | 0;
    this.h[5] = (this.h[5]! + f) | 0;
    this.h[6] = (this.h[6]! + g) | 0;
    this.h[7] = (this.h[7]! + h) | 0;
  }
}

/** Hash a File without holding more than one chunk in memory at a time. */
export async function sha256File(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const hash = new Sha256();
  const reader = file.stream().getReader();
  let read = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    hash.update(value);
    read += value.length;
    onProgress?.(file.size > 0 ? read / file.size : 1);
  }

  onProgress?.(1);
  return hash.digest();
}

/** Exposed for the test page; the file path above is the one that matters. */
export function sha256Bytes(data: Uint8Array): string {
  const hash = new Sha256();
  hash.update(data);
  return hash.digest();
}
